import { useState, useMemo } from "react";
import { Layout } from "@/components/layout/Layout";
import { SeoBreadcrumb } from "@/components/SeoBreadcrumb";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { articles } from "@/data/articles";
import {
  blogCategories,
  getBlogCategoryForArticle,
  type BlogCategorySlug,
} from "@/data/blogCategories";
import { ArrowRight, ArrowLeft, Clock, Calendar, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const PAGE_SIZE = 12;

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<BlogCategorySlug | "all">("all");
  const [page, setPage] = useState(1);

  const sortedArticles = useMemo(
    () =>
      articles
        .filter((a) => new Date(a.publishedAt).getTime() <= Date.now())
        .sort(
          (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        ),
    []
  );

  const filteredArticles = useMemo(() => {
    return sortedArticles.filter((article) => {
      if (activeCategory !== "all") {
        const cat = getBlogCategoryForArticle(article.category);
        if (cat.slug !== activeCategory) return false;
      }
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        article.title.toLowerCase().includes(query) ||
        article.excerpt.toLowerCase().includes(query) ||
        article.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    });
  }, [sortedArticles, searchQuery, activeCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageArticles = filteredArticles.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleCategory = (slug: BlogCategorySlug | "all") => {
    setActiveCategory(slug);
    setPage(1);
  };

  return (
    <Layout>
      <Helmet>
        <title>Blog Taekwondo Mattei | Disciplina, Bambini e Adulti</title>
        <meta name="description" content="Articoli, guide e cronache di gara dal Taekwondo Mattei a Roma: bambini, adulti, tecnica, agonismo, salute e benefici della pratica." />
        <link rel="canonical" href="https://taekwondomattei.com/blog" />
        <meta property="og:title" content="Blog Taekwondo Mattei | Disciplina, Bambini e Adulti" />

        <meta property="og:description" content="Articoli, guide e cronache di gara dal Taekwondo Mattei a Roma." />
        <meta property="og:image" content="https://taekwondomattei.com/og-home.jpg" />
        <meta property="og:url" content="https://taekwondomattei.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Blog Taekwondo Mattei",
          description: "Articoli, guide e cronache di gara dal Taekwondo Mattei a Roma.",
          url: "https://taekwondomattei.com/blog",
          publisher: { "@type": "Organization", name: "Taekwondo Mattei" },
        })}</script>
      </Helmet>

      <SeoBreadcrumb items={[{ label: "Blog" }]} />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="container-wide mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Blog Taekwondo Mattei
            </h1>
            <p className="font-body text-xl text-muted-foreground">
              Articoli su disciplina, bambini, adulti, tecnica e agonismo. Scritti dai nostri maestri a Roma.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-background border-b border-border">
        <div className="container-wide mx-auto px-4 md:px-8 space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleCategory("all")}
              className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                activeCategory === "all"
                  ? "bg-gold text-black border-gold"
                  : "border-border hover:border-gold"
              }`}
            >
              Tutti
            </button>
            {blogCategories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => handleCategory(c.slug)}
                className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                  activeCategory === c.slug
                    ? "bg-gold text-black border-gold"
                    : "border-border hover:border-gold"
                }`}
              >
                {c.shortLabel}
              </button>
            ))}
          </div>

          <div className="max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Cerca un articolo..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Cancella ricerca"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-body text-lg text-muted-foreground">
                Nessun articolo trovato.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {pageArticles.map((article) => {
                  const cat = getBlogCategoryForArticle(article.category);
                  return (
                    <Link
                      key={article.id}
                      to={`/blog/${article.slug}`}
                      className="group card-elevated border border-border overflow-hidden"
                    >
                      <div className="p-6 space-y-4">
                        <div className="flex items-center gap-3">
                          <span className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                            {cat.shortLabel}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {article.readTime} min
                          </span>
                        </div>

                        <h2 className="font-display text-xl font-semibold text-foreground group-hover:text-gold transition-colors line-clamp-2">
                          {article.title}
                        </h2>

                        <p className="font-body text-muted-foreground text-sm leading-relaxed line-clamp-3">
                          {article.excerpt}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-border">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {new Date(article.publishedAt).toLocaleDateString("it-IT", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <span className="font-body text-sm font-medium text-gold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                            Leggi <ArrowRight size={14} />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav
                  className="mt-12 flex items-center justify-center gap-2"
                  aria-label="Paginazione articoli"
                >
                  <button
                    type="button"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-full border border-border disabled:opacity-40 hover:border-gold transition-colors"
                    aria-label="Pagina precedente"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                        p === currentPage
                          ? "bg-gold text-black"
                          : "border border-border hover:border-gold"
                      }`}
                      aria-current={p === currentPage ? "page" : undefined}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-full border border-border disabled:opacity-40 hover:border-gold transition-colors"
                    aria-label="Pagina successiva"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding gradient-black">
        <div className="container-narrow mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gold mb-6">
            Vuoi saperne di più?
          </h2>
          <p className="font-body text-lg text-gold/70 mb-8 max-w-xl mx-auto">
            La teoria è importante, ma la pratica lo è di più. Vieni a provare una lezione gratuita.
          </p>
          <Link to="/lezione-di-prova" className="btn-accent text-lg">
            Prenota una lezione di prova
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
